import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/utils";
import {
  businessDay, businessRange, businessToday, dailyTotals, ackState, describeChange, periodLabel, PERIOD_STATUS_LABEL,
  type TimeLogLite, type AuditRowLite,
} from "@/lib/time-period";

interface Period { id: string; start_date: string; end_date: string; status: "open" | "approved" | "exported" | "accepted"; approved_at: string | null }
interface Pto { start_date: string; end_date: string; reason: string | null }

/**
 * An employee reviews their own pay period and confirms it.
 *
 * HQ-PAYROLL-001. Workflow map, Worked time to approved payroll, step 3: "show daily
 * totals, edits, missed punches, PTO, exceptions, and an explicit acknowledgment
 * before submission". The employee saw a flat list of logs and was never asked whether
 * it was right. Days are the BUSINESS's days, the same ones payroll will use, so what
 * they confirm is what gets paid. Confirming is recorded with the hours at that moment:
 * if anything changes afterwards the card says so, and asks again.
 *
 * Renders nothing when there is no period to review, and nothing if the lookup fails —
 * a broken review card must never get between a person and their clock.
 */
export function MyPayPeriodCard() {
  const { employee } = useAuth();
  const [period, setPeriod] = useState<Period | null>(null);
  const [logs, setLogs] = useState<TimeLogLite[]>([]);
  const [changes, setChanges] = useState<AuditRowLite[]>([]);
  const [pto, setPto] = useState<Pto[]>([]);
  const [ack, setAck] = useState<{ hours: number; acknowledged_at: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!employee) return;
    const today = businessToday();
    const { data: ps } = await supabase.from("hq_pay_periods").select("id,start_date,end_date,status,approved_at")
      .lte("start_date", today).order("start_date", { ascending: false }).limit(1);
    const p = ((ps as Period[] | null) ?? [])[0] ?? null;
    setPeriod(p);
    if (!p) return;
    const { startIso, endIso } = businessRange(p.start_date, p.end_date);
    const [lg, au, pt, ak] = await Promise.all([
      supabase.from("hq_time_logs").select("id,employee_id,clock_in_time,clock_out_time,hours_worked,manual_entry,verification_status,notes")
        .eq("employee_id", employee.id).gte("clock_in_time", startIso).lt("clock_in_time", endIso).order("clock_in_time"),
      supabase.from("hq_time_log_audit").select("*").eq("employee_id", employee.id).order("created_at", { ascending: false }).limit(100),
      supabase.from("hq_pto_requests").select("start_date,end_date,reason")
        .eq("employee_id", employee.id).eq("request_status", "approved").lte("start_date", p.end_date).gte("end_date", p.start_date),
      supabase.from("hq_time_acknowledgments").select("hours,acknowledged_at").eq("period_id", p.id).eq("employee_id", employee.id).maybeSingle(),
    ]);
    setLogs((lg.data as TimeLogLite[] | null) ?? []);
    // Changes made by SOMEONE ELSE to time in this period; the employee's own entries are theirs.
    setChanges(((au.data as AuditRowLite[] | null) ?? []).filter(r => {
      const at = (r.after ?? r.before) as { clock_in_time?: string } | null;
      return r.changed_by !== employee.id && !!at?.clock_in_time && at.clock_in_time >= startIso && at.clock_in_time < endIso;
    }));
    setPto((pt.data as Pto[] | null) ?? []);
    setAck((ak.data as { hours: number; acknowledged_at: string } | null) ?? null);
  }
  useEffect(() => { void load().catch(() => { /* not shown rather than shown broken */ }); }, [employee?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function confirmHours() {
    if (!period) return;
    setBusy(true); setError(null);
    const { error: err } = await supabase.rpc("hq_time_acknowledge", { p_period_id: period.id });
    setBusy(false);
    if (err) { setError(err.message); return; }
    await load();
  }

  if (!period) return null;
  const days = dailyTotals(logs);
  const total = days.reduce((s, d) => s + d.hours, 0);
  const state = ackState(ack, total);
  const open = period.status === "open";
  const missed = days.filter(d => d.open > 0);

  return (
    <section className="card space-y-3" aria-labelledby="my-pay-period">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="my-pay-period" className="text-base font-semibold">Your hours for {periodLabel(period.start_date, period.end_date)}</h2>
        <span className={`pill ${open ? "pill-warn" : "pill-ok"} text-[10px]`}>{open ? "Please review" : PERIOD_STATUS_LABEL[period.status]}</span>
      </div>

      {days.length === 0 ? (
        <p className="text-sm text-gray2">No time recorded in this period.</p>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left text-xs uppercase tracking-wider text-gray2"><th className="py-1">Day</th><th className="text-right">Hours</th><th /></tr></thead>
          <tbody>
            {days.map(d => (
              <tr key={d.day} className="border-b border-line/60">
                <td className="py-1.5">{new Date(`${d.day}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</td>
                <td className="py-1.5 text-right font-medium">{d.hours.toFixed(2)}</td>
                <td className="py-1.5 pl-3 text-xs text-orange-900">{d.open > 0 ? "No clock-out recorded" : ""}</td>
              </tr>
            ))}
            <tr><td className="pt-2 font-semibold">Total</td><td className="pt-2 text-right font-semibold">{total.toFixed(2)}</td><td /></tr>
          </tbody>
        </table>
      )}

      {missed.length > 0 && open && (
        <p className="rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">
          You have {missed.length === 1 ? "a day" : `${missed.length} days`} with no clock-out. Add the missing time under Manual Entry, or tell your manager, before you confirm.
        </p>
      )}

      {pto.length > 0 && (
        <div className="text-sm">
          <div className="font-medium">Approved time off in this period</div>
          <ul className="text-gray2">{pto.map((x, i) => <li key={i}>{businessDay(`${x.start_date}T12:00:00Z`)} to {businessDay(`${x.end_date}T12:00:00Z`)}{x.reason ? ` — ${x.reason}` : ""}</li>)}</ul>
        </div>
      )}

      {changes.length > 0 && (
        <div className="text-sm">
          <div className="font-medium">Changes someone else made to your time</div>
          <ul className="mt-1 space-y-2">
            {changes.map(r => (
              <li key={r.id} className="rounded-lg bg-offwhite px-3 py-2">
                <div className="text-xs text-gray2">{fmtDate(r.created_at)}</div>
                <ul className="list-disc pl-5">{describeChange(r).map((l, i) => <li key={i}>{l}</li>)}</ul>
                {r.reason && <div className="text-xs"><strong>Why:</strong> {r.reason}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <div role="alert" className="rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{error}</div>}

      {open ? (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="btn" disabled={busy || state === "current"} onClick={() => void confirmHours()}>
            {state === "current" ? "✓ You confirmed these hours" : state === "changed" ? "Confirm the new total" : "These hours are correct"}
          </button>
          {state === "current" && ack && <span className="text-xs text-gray2">{fmtDate(ack.acknowledged_at)}</span>}
          {state === "changed" && <span className="text-sm text-warn">Your hours changed after you confirmed them ({Number(ack!.hours).toFixed(2)} → {total.toFixed(2)}).</span>}
          {state === "none" && <span className="text-xs text-gray2">If something is wrong, tell your manager before payroll is approved.</span>}
        </div>
      ) : (
        <p className="text-sm text-gray2">This period was approved{period.approved_at ? ` on ${fmtDate(period.approved_at)}` : ""}. Questions about it go to your manager.</p>
      )}
    </section>
  );
}
